import { FormItemProps } from '../templates/FormProps';
import useRoleMemo from '@vindr/utils/hooks/useRoleMemo';
import { customSearch } from '@vindr/utils/helpers/common.helper';
import { Form, Select, SelectProps } from 'antd';
import type { InternalFieldProps } from 'rc-field-form/lib/Field';
import { useTranslation } from 'react-i18next';

export type FormSelectProps<T> = Omit<SelectProps, 'children'> &
  Omit<InternalFieldProps, 'name' | 'children'> &
  FormItemProps<T> & {
    children?: JSX.Element | JSX.Element[];
    searchVietnamese?: boolean;
    allowClear?: boolean;
    options?: SelectProps['options'];
    wrapperClassName?: string;
    className?: string;
    formProps?: Parameters<typeof Form['Item']>['0'];
  };

const FormSelect = <T,>({
  name = '',
  label = '',
  placeholder = '',
  rules,
  children,
  loading,
  disabled,
  searchVietnamese = true,
  allowClear = true,
  required,
  options,
  className,
  noStyle,
  wrapperClassName,
  formProps,
  ...props
}: FormSelectProps<T>) => {
  const { t } = useTranslation('Vindoc');

  const rulesMemo = useRoleMemo({ required, rules, disabled });

  return (
    <Form.Item
      name={name}
      {...(label && { label: t(label) })}
      className={wrapperClassName}
      noStyle={noStyle}
      rules={rulesMemo}
      {...formProps}
    >
      <Select
        allowClear={allowClear}
        className={className}
        disabled={disabled}
        dropdownMatchSelectWidth={false}
        filterOption={(input, option) => customSearch(input, option, searchVietnamese, t)}
        getPopupContainer={(trigger) => trigger.parentElement} // make the popup list fix position with parent on scroll
        options={options}
        placeholder={t(placeholder)}
        {...props}
      >
        {!options && children}
      </Select>
    </Form.Item>
  );
};

export default FormSelect;
